import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { track, trackFailed } from '../../../../../shared-web/track';
import { stashSay } from '../../../../../shared-web/say-handoff';
import { noteVisitorConsent, useVisitorConsent } from '../../../../../shared-web/inbound-door';
import { emberApi, type HomeTail, type TreeNode } from '../api';
import { navigate } from '../router';
import { useTellingDoors, type TalkSheetRequest } from '../talk-sheet';
import { CallGlyph } from '../components/CallGlyph';
import { HeroTail } from './HeroTail';
import { SEARCH_SETTLE_MS } from './TreeSearch';
import { ConsentTerms, CONSENT_AGE, CONSENT_NOTE } from '../components/ConsentTerms';

const HOME_REQUEST: TalkSheetRequest = { entryType: 'home', returnTo: '/' };

/**
 * Rows in the hot list: the window's crowded share — the lights the most
 * people stood at, which the home payload already carries (LIFE_NODES
 * VIEW_SIZE 40 × TODAY_CROWDED_SHARE 0.7); the daily random draw stays out.
 * 2026-09-15 拍板: eight rows was an arbitrary cut on data already in hand,
 * and a paged list was turned down — past ~25 rows every light is one or two
 * people, and typing is the way to the long tail.
 */
const HOT_LIMIT = 28;
/** Rows the search may list: the server's LIGHT_SEARCH.LIMIT, restated. */
const SEARCH_LIMIT = 8;
// The list follows the words after SEARCH_SETTLE_MS (TreeSearch): one settle for
// both searches, 700 ms since 2026-10-22 (the reason is there).
/**
 * Rows the list shows before it scrolls (2026-10-16 jam 拍板): three and the
 * top half of a fourth, which says there is more. All 28 rows stood open on a
 * tall screen and took the whole height above the docked bar.
 */
const LIST_PEEK_ROWS = 3.5;
/** The page held still this long after a scroll: the sheet comes back. */
const SCROLL_SETTLE_MS = 300;
/** The next step's glow after a pick or a filled field: three breaths (TreeHome.css .hero-next), then quiet. */
const GLOW_MS = 2600;
/** Words in the field and the hands still this long: the sentence counts as filled in, and the next step lights up. */
const FILLED_PAUSE_MS = 3000;
/** The glow is for a browser in its first week here (2026-09-16 拍板): the day this composer was first on its screen, kept in localStorage. */
const FIRST_HERE_KEY = 'ember.hero.first_here';
const NEW_HERE_MS = 7 * 24 * 60 * 60 * 1000;
const noteFirstHere = (): void => { try { if (!window.localStorage.getItem(FIRST_HERE_KEY)) window.localStorage.setItem(FIRST_HERE_KEY, new Date().toISOString()); } catch { /* then every visit is the first week */ } };
const newHere = (): boolean => {
  try {
    const first = window.localStorage.getItem(FIRST_HERE_KEY);
    return !first || Date.now() - Date.parse(first) < NEW_HERE_MS;
  } catch { return true; }
};

/**
 * The first screen's door is a composer (2026-09-12 拍板): the first sentence
 * typed under the tree IS the first turn of the conversation. No sheet, no
 * choice to make before a word is said — the sentence goes to the chat page
 * as `say` and is sent on arrival (Talk.tsx).
 *
 * Two doors in the field's right end, both always there and both usable
 * with or without a sentence (2026-09-12 夜 拍板): the arrow is the chat —
 * with a sentence it sends it as the first turn, empty it opens the
 * conversation and Ember speaks first; the handset is the in-browser call,
 * and it takes the sentence along when there is one (`say` on /collect, the
 * call opens by meeting what they wrote). Nothing changes shape or meaning
 * under the finger. The arrow is drawn as the primary: it is what Enter
 * does and where nearly everyone goes; the handset is the same size, quieter.
 * (Earlier that day: one button that swapped handset↔send with the field's
 * contents, then an arrow that unfolded two chips on an empty field — both
 * asked the person to learn something before saying anything.)
 *
 * The list of lights (2026-09-13 拍板): tapped while empty, the field raises
 * the lights the most people have stood at (today's window's crowded share,
 * hottest first — 28 rows since 2026-09-15, in one native scroll);
 * as the person types, the list becomes the lights their words could mean —
 * the canopy's own search. A row is not a light's title: it is the light's
 * first line, the sentence a person standing there would type first, so the
 * person scanning the list is looking for themself. Choosing a row puts that
 * sentence into the field as their own opening — nothing is sent; the doors
 * still take whatever is in the field. From then on the list stays down —
 * the sentence is theirs to finish, not a query — until the field is emptied.
 * The next step lights up (three amber breaths on both doors; on the consent
 * boxes first when they are unticked) once per filling — after a pick, or
 * once there are words and the hands have been still three seconds — for a
 * browser in its first week here (2026-09-16 拍板; until then only after a
 * pick and only until a door was taken once): a filled field alone did not
 * say what to do next. No light
 * close enough → no list: the field itself is the way out, so there is no
 * "none of these" row here.
 * (The four tapped first sentences dropped on 2026-09-12 were made-up lines
 * anyone might tap out of curiosity; these are real places on the tree, each
 * with how many stood there, and a tap only fills the field.)
 * On a light's own page (2026-09-24 拍板) the composer is that page's telling
 * door and `here` is its light: the hot list opens on that light's first line,
 * then the same hottest lights in the same order as under the tree.
 *
 * The empty field asks (placeholder). 2026-09-15 to 09-16 it showed the five
 * hottest lights in turn instead; per-person timelines showed the row read
 * as a display and taps on the field fell from 50% to 8% on the same ad, so
 * the placeholder is back (2026-09-16 拍板).
 *
 * The thread's tail (2026-09-16 拍板, §0.8 A): a browser or account that has
 * already spoken to Ember (`tail`, from /api/tree/you — guest by cookie,
 * member by account, one rule) sees Ember's last line and when, in the slot
 * the supporting sentence holds for a stranger (TreeHome drops that
 * sentence). The line is a door: tapped, it goes to the conversation, with
 * whatever is in the field as the next turn — exactly what the arrow does.
 * The field takes the return placeholder: both readings of it, said once.
 * Shape and motion (same day, second decision, after three mocks): the line
 * sits on top of the field as a quote bar — one rounded shape with the field,
 * amber rule at its left, chevron at its right — so the composer visibly is
 * the conversation's tail; nothing has to say so. On arrival the line rises
 * out of the field once and settles (the message-app send motion, reversed:
 * this came from the conversation behind the field), then the rule breathes
 * on the fireflies' rhythm. Words never move; reduced motion gets the shape
 * alone. A light running the edge was looked at and set aside: it points at
 * the box, not at what it is.
 * Since 2026-10-20 (founder, mock home-ticker option A) the bar carries every
 * conversation with something unread — Ember's and the rooms' — in turn,
 * each with its picture, name and who said what; a tap opens the one showing
 * (HeroTail). With nothing unread it is Ember's last line, as before.
 * Per-person prod timelines after the 09-14 rollout: people back with
 * nothing new pressed a door on the empty field (which works, but nothing
 * said it would) or went through the menu and /me for the way back; one
 * picked the same list row twice into the same conversation. The two doors
 * are unchanged, so people back with something new lose nothing.
 *
 * The same doors as TellingDoors, behind the same gates: a signed-out browser
 * that has not ticked 18+ and the Terms is asked here, on the door — once it
 * has a sentence to send (a row picked) or presses a door, never on the first
 * tap. The record is written before the page moves, so the chat opens without
 * asking. A spent free budget routes both doors to the registration chooser,
 * as everywhere else.
 *
 * The sheet (2026-10-16 jam 拍板, mock https://claude.ai/artifact/R4g5xToh2FSV43UyaoBTjb,
 * option A): the list and the acknowledgments are one night-paper sheet that
 * grows up out of the field — the field is its bottom edge — and take turns
 * in it, never both at once. The list comes first: tapped empty, the field
 * raises it; a pick puts it away and, for a browser still to tick, the boxes
 * rise in its place with the keyboard left where it is. (2026-10-15 had the
 * boxes first and the list only after them; the first tap asked for consent
 * before anything was offered.) Ticked, the boxes stay up, checked, until a
 * door is taken — and with a sentence in the field, the second tick takes it
 * (2026-10-21 jam): the door they pressed, else the chat. What folds the
 * sheet: the list, a blur with the field empty or a scroll; the boxes, a
 * blur or a scroll — but only while nothing is picked and nothing is ticked; a picked sentence or one tick keeps them up
 * through both, docked with the bar on the bottom edge once the keyboard is
 * gone. Folded by a scroll, the sheet comes back when the page holds still.
 *
 * Docked (`dock`, the homepage on a phone, 2026-10-15 拍板, third try that
 * day): the composer is a bar fixed to the bottom edge, where every chat
 * app's and mobile search's field is — in the first screen, down the page,
 * and under the keyboard, one position, nothing that changes shape under a
 * finger. (Earlier that day: the field in the ground band, scrolled onto the
 * keyboard by us after Safari's own reveal — the two fought and bounced; then
 * lifting out of the band on the tap — the field moved while the finger
 * was down, iOS judged the tap against what was under the finger when it
 * lifted, and no keyboard came. Both retired.) A field fixed to the bottom
 * edge is one Safari lifts onto the keyboard by itself (the Talk page's
 * contract). The ground band keeps the bar's room at its foot (TreeHome.css
 * `--hero-dock-h`), the trust line stands above the bar, the consent boxes
 * and the list rise from it. It took the place of the door bar (Join Ember ·
 * Talk to Ember, 2026-10-10 to 10-15).
 */
interface HeroComposerProps {
  placeholder: string;
  /** The field's placeholder when the tail shows. */
  returnPlaceholder?: string;
  /** The bar over the field (/api/tree/you `tail`): this person's unread conversations, or Ember's last line; null or no rows for a stranger. */
  tail?: HomeTail | null;
  lights?: TreeNode[];
  /** The light this composer stands at (a Place page): first in the hot list. */
  here?: TreeNode | null;
  /** Where the conversation starts from: the homepage by default; a Place page passes its light (entryType 'node'). */
  request?: TalkSheetRequest;
  /** The doors' placement in the log: hero on the homepage, place on a Place page (place_closing, the closing band's second instance, to 2026-09-25). */
  discovery?: string;
  /**
   * A sentence already in the field on arrival, theirs to finish — a Place
   * page reached from a Google ad puts the words they searched here
   * (searched-opening.ts, 2026-10-04 拍板). Taken once, while the field is
   * still empty, the way a picked row is: the list stays down until the
   * field is emptied, nothing is sent, the keyboard is not raised.
   */
  opening?: string;
  /** A bar fixed to the bottom edge on a phone (the homepage). */
  dock?: boolean;
}

export function HeroComposer({
  placeholder, returnPlaceholder = '', tail = null, lights = [], here = null, request = HOME_REQUEST, discovery = 'hero', opening = '', dock = false,
}: HeroComposerProps): ReactElement {
  const { spent, chatTo, callHref, registerTo, doorLogAttrs } = useTellingDoors(request);
  // Docked: a phone-width screen.
  const [phone, setPhone] = useState(false);
  const docked = dock && phone;
  // The doors' own facts (talk_choose_message / talk_choose_call), placed:
  // discovery=hero tells this composer apart from the sheet's doors
  // (discovery=home) without a second name.
  const heroLog = { ...doorLogAttrs, 'data-log-discovery': discovery };
  const shown = tail?.rows.length ? tail : null;
  const { status } = useSession();
  const consent = useVisitorConsent();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const hostRef = useRef<HTMLFormElement>(null);
  const [text, setText] = useState('');
  const [ageOk, setAgeOk] = useState(false);
  const [tosOk, setTosOk] = useState(false);
  const asking = status === 'anonymous' && consent.known && !consent.consented && !spent;
  const waiting = asking && !(ageOk && tosOk);
  // The record is still being read (TellingDoors' rule, dev e2e 2026-10-19): a door pressed now is kept and pressed
  // again for them when the answer comes — opening the chat first had it refuse the telling and bounce back.
  const settling = status === 'anonymous' && !consent.known && !spent;
  const [pendingDoor, setPendingDoor] = useState<'write' | 'call' | null>(null);
  const said = text.trim();

  // `open`: the composer is in use — from the field's focus until it is let
  // go (a blur, a tap elsewhere), and kept past that while there are words
  // in the field. `found` is the search's answer to the current words (null
  // = nothing searched yet, or the field is empty and the hottest lights
  // stand in).
  const [open, setOpen] = useState(false);
  const [found, setFound] = useState<TreeNode[] | null>(null);
  // The latest search wins: a slower answer to earlier words is dropped.
  const searchSeq = useRef(0);
  // The last words searched and their answer (2026-10-21 Riley). The search
  // effect follows `open` as well as the words, so every focus, blur and tap
  // elsewhere re-ran it — and asked the edge again for the same words each
  // time (prod 10-20, one hour: one browser, the identical text, 63 times;
  // each a Vertex call and three queries). Unchanged words take the stored
  // answer; changed words search as before. A failed search is not stored.
  const lastSearch = useRef<{ words: string; nodes: TreeNode[] } | null>(null);
  // After a pick the sentence is theirs to finish: the list stays down —
  // focusing or adding words does not raise it — until the field is emptied.
  const [picked, setPicked] = useState(false);
  // A door pressed while the boxes wait: the boxes are what it asks for next.
  const [asked, setAsked] = useState(false);
  // Which door that was — the one taken for them once both boxes are ticked.
  const askedFor = useRef<'write' | 'call'>('write');
  // The person's hands are on the composer — the field focused, a door just
  // pressed — and not yet let go: boxes raised only by a door fold when it is.
  const [held, setHeld] = useState(false);
  // The person is scrolling the page: the sheet is out of the way until it holds still.
  const [scrolling, setScrolling] = useState(false);
  // The next step lights up after a pick or a filled field (2026-09-13 拍板,
  // widened 2026-09-16): the two doors, or the consent boxes while they are
  // unticked (then the doors once they are). Once per filling (`lit`, back
  // to false when the field is emptied), for a browser in its first week here.
  const [glow, setGlow] = useState<'doors' | 'consent' | null>(null);
  const [lit, setLit] = useState(false);

  /** The hottest lights of today's window, only those with a first line to offer — after the light this composer stands at, when there is one. */
  const hot = useMemo(() => {
    // On a homepage track (tracks.ts) the track's own lights come first, then
    // the rest by crowd as always.
    const hottest = lights.filter((n) => n.first_line && n.id !== here?.id)
      .sort((a, b) => (Number(Boolean(b.track)) - Number(Boolean(a.track))) || (b.people_count - a.people_count))
      .slice(0, HOT_LIMIT);
    return here?.first_line ? [here, ...hottest] : hottest;
  }, [lights, here]);
  const mode: 'hot' | 'search' = said ? 'search' : 'hot';
  const rows = said ? (found ?? []) : hot;

  // What the sheet holds (2026-10-16): the list while the field is in use and
  // has nothing chosen yet; the boxes once there is a sentence to send or a
  // door was pressed. A picked sentence or one tick keeps the boxes up
  // through a blur and a scroll; otherwise both fold them.
  const live = open && !scrolling;
  const searching = live && !!said && !picked && !asked;
  const panel: 'list' | 'consent' | null = (() => {
    if (live && !said && !asked && hot.length > 0) return 'list';
    if (searching && rows.length > 0) return 'list';
    if (!asking) return null;
    // `open` with the pick: words that arrived in the field (`opening`) do not
    // raise the boxes before the person has touched the composer.
    if ((picked && open) || ageOk || tosOk) return 'consent';
    return asked && held && !scrolling ? 'consent' : null;
  })();

  useEffect(() => { noteFirstHere(); }, []);

  useEffect(() => { if (!text) { setPicked(false); setAsked(false); setLit(false); } }, [text]);

  // The searched words, once, into an empty field: from here the sentence
  // is theirs, exactly as after a pick.
  useEffect(() => {
    if (!opening || text) return;
    setText(opening);
    setPicked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening]);

  // The field grows with the words (2026-10-26 founder), up to the rows its max-height allows, then scrolls; the doors
  // stay at its middle (.hero-field centres them). Docking changes its width, so the rows too.
  useLayoutEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, docked]);

  /** This filling's one light on the next step: the doors, or the boxes while they wait. */
  const lightNext = (): void => {
    if (lit || !newHere()) return;
    setLit(true);
    setGlow(waiting ? 'consent' : 'doors');
  };

  // Words in the field, hands still for a while: the sentence is filled in.
  useEffect(() => {
    if (!said || lit) return undefined;
    const t = window.setTimeout(lightNext, FILLED_PAUSE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [said, lit, waiting]);

  useEffect(() => {
    if (glow === null) return undefined;
    const t = window.setTimeout(() => setGlow(null), GLOW_MS);
    return () => window.clearTimeout(t);
  }, [glow]);

  // Both boxes ticked: the light moves on to the doors, which are what is
  // pressed next — the boxes stay up, checked, beside them.
  const wasWaiting = useRef(waiting);
  useEffect(() => {
    if (wasWaiting.current && !waiting && asking && (glow === 'consent' || newHere())) setGlow('doors');
    wasWaiting.current = waiting;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  useEffect(() => {
    if (!open || !said || picked || asked) { setFound(null); return undefined; }
    const seq = ++searchSeq.current;
    const words = said.replace(/\s+/g, ' ');
    const last = lastSearch.current;
    if (last && last.words === words) { setFound(last.nodes); return undefined; }
    const t = window.setTimeout(() => {
      emberApi.treeSearch(said)
        .then(({ nodes }) => {
          if (seq !== searchSeq.current) return;
          const listed = nodes.filter((n) => n.first_line).slice(0, SEARCH_LIMIT);
          lastSearch.current = { words, nodes: listed };
          setFound(listed);
        })
        .catch(() => { if (seq === searchSeq.current) { trackFailed('hero_light_search', 'load_failed'); setFound([]); } });
    }, SEARCH_SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [said, open, picked, asked]);

  useEffect(() => {
    if (!dock) return undefined;
    const mq = window.matchMedia('(max-width: 799px)');
    const onChange = (): void => setPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [dock]);

  // Docked: the bar's height, for the ground band to keep room at its foot
  // (`--hero-dock-h`), and the cookie notice rides the bar's top edge instead
  // of covering it (CookieBanner.tsx's --cookie-banner-lift, as under the
  // door bar before it).
  useEffect(() => {
    const host = hostRef.current;
    if (!docked || !host) return undefined;
    const measure = (): void => {
      document.body.style.setProperty('--hero-dock-h', `${host.offsetHeight}px`);
      document.body.style.setProperty('--cookie-banner-lift', `calc(${host.offsetHeight + 16}px + env(safe-area-inset-bottom, 0px))`);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(host);
    return () => {
      ro?.disconnect();
      document.body.style.removeProperty('--hero-dock-h');
      document.body.style.removeProperty('--cookie-banner-lift');
    };
  }, [docked]);

  // The list's room (2026-10-15): never taller than what is seen between the
  // field and the top of the visual viewport — or the bottom of a top bar
  // standing there (data-eh-top-bar) — so it is never cut off under the
  // screen's top edge. Docked, the list takes the whole height above the bar
  // (it draws over the nav — while a sentence is being chosen the nav is not
  // what the screen is for; a phone with the keyboard up left three rows
  // otherwise), and the room is the visual viewport's height less the bar's
  // — no element coordinates: with the keyboard up Safari pans the page, and
  // a fixed element's rect and the viewport's offsetTop then disagree about
  // where zero is (the difference came out negative; two rows showed, Riley's
  // iPhone 2026-10-15). Re-measured as the page or the viewport moves. Under
  // that room the list keeps to its own height of three and a half rows
  // (LIST_PEEK_ROWS, `--hero-list-cap`, measured below).
  const listUp = panel === 'list';
  useEffect(() => {
    const host = hostRef.current;
    if (!listUp || !host) return undefined;
    const vv = window.visualViewport;
    let frame = 0;
    const measure = (): void => {
      frame = 0;
      // The sheet's own 6px above the list and 6px under it (.hero-sheet), then air above it.
      if (docked) {
        // The bar stands on the viewport's bottom edge (8px over it while
        // typing); 8px of air above the list, under the status bar where the
        // viewport runs under it (the home-screen app — TreeHome.css takes
        // the safe-area inset off).
        const vvH = vv?.height ?? window.innerHeight;
        host.style.setProperty('--hero-list-room', `${Math.round(vvH - host.offsetHeight - 8 - 18 - 12)}px`);
        return;
      }
      const shell = host.querySelector('.hero-field');
      if (!shell) return;
      let top = vv?.offsetTop ?? 0;
      document.querySelectorAll('[data-eh-top-bar]').forEach((bar) => { top = Math.max(top, bar.getBoundingClientRect().bottom); });
      host.style.setProperty('--hero-list-room', `${Math.round(shell.getBoundingClientRect().top - top - 22 - 12)}px`);
    };
    const onMove = (): void => { if (!frame) frame = window.requestAnimationFrame(measure); };
    measure();
    // The keyboard's rise is an animation and Safari's viewport events can
    // land mid-way (a mid-animation height was the last one read: the list
    // stopped well short of the top, Riley's iPhone 2026-10-15): a few late
    // readings after the list opens catch the settled geometry.
    const late = [350, 700, 1100].map((ms) => window.setTimeout(measure, ms));
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    vv?.addEventListener('resize', onMove);
    vv?.addEventListener('scroll', onMove);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      late.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
      vv?.removeEventListener('resize', onMove);
      vv?.removeEventListener('scroll', onMove);
    };
  }, [listUp, docked]);

  // The list's own height: three rows and the top half of a fourth, as the
  // rows came out (a first line takes one line or two at this width).
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const items = listUp ? host.querySelectorAll<HTMLElement>('.hero-list-row') : [];
    if (items.length <= Math.ceil(LIST_PEEK_ROWS)) { host.style.removeProperty('--hero-list-cap'); return; }
    let cap = 0;
    for (let i = 0; i < Math.floor(LIST_PEEK_ROWS); i += 1) cap += items[i].offsetHeight;
    cap += items[Math.floor(LIST_PEEK_ROWS)].offsetHeight * (LIST_PEEK_ROWS % 1);
    host.style.setProperty('--hero-list-cap', `${Math.round(cap)}px`);
  }, [listUp, rows]);

  // The sheet's foot is the field (.hero-sheet's bottom padding,
  // `--hero-field-h`): the field's own height, re-read as the words grow it,
  // so a long first sentence does not cover the sheet's last rows — the
  // consent boxes (prod 2026-10-29: at 412×800 three lines covered "I agree
  // to the Terms", five lines both boxes; the padding had read the variable
  // since 10-16 and nothing set it, so it held one line's 56px).
  useLayoutEffect(() => {
    const host = hostRef.current;
    const shell = host?.querySelector<HTMLElement>('.hero-field');
    if (!panel || !host || !shell) return undefined;
    const measure = (): void => { host.style.setProperty('--hero-field-h', `${shell.offsetHeight}px`); };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(shell);
    return () => {
      ro?.disconnect();
      host.style.removeProperty('--hero-field-h');
    };
  }, [panel]);

  // The person scrolling the page (a drag or a wheel anywhere but the
  // composer; the list's own scroll is inside it): the sheet folds, and
  // comes back once the page — momentum included — has held still. A
  // finger, not a scroll event, is what starts it: Safari scrolls the
  // document itself to bring a focused field clear of the keyboard.
  useEffect(() => {
    let settle = 0;
    let on = false;
    const still = (): void => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => { on = false; setScrolling(false); }, SCROLL_SETTLE_MS);
    };
    const onGesture = (e: Event): void => {
      if (hostRef.current?.contains(e.target as Node)) return;
      // A scroll lets the composer go as a blur does (2026-10-16 jam: one rule
      // for both) — a finger's drag already did, at its press; a wheel has no press.
      if (!on) { on = true; setScrolling(true); setHeld(false); }
      still();
    };
    const onScroll = (): void => { if (on) still(); };
    document.addEventListener('touchmove', onGesture, { passive: true });
    document.addEventListener('wheel', onGesture, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(settle);
      document.removeEventListener('touchmove', onGesture);
      document.removeEventListener('wheel', onGesture);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  /** The composer let go: an empty field folds the list; words in the field keep what is up. */
  const letGo = (): void => { setOpen(!!fieldRef.current?.value.trim()); setHeld(false); };

  // A tap anywhere but the composer lets it go. So does the field letting go
  // of the focus (2026-10-15: the keyboard's own ✓ dismisses it with no tap
  // on the page, and the list stayed up over the page) — unless the focus
  // went because a finger is down on the composer itself (a row, a door: a
  // mouse's press on a row blurs the field before the row's click).
  const pressInside = useRef(false);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent): void => {
      pressInside.current = !!hostRef.current?.contains(e.target as Node);
      if (!pressInside.current) letGo();
    };
    const onUp = (): void => { pressInside.current = false; };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const onFieldBlur = (): void => {
    if (!pressInside.current) letGo();
    restoreScroll();
  };

  // Docked, the keyboard going: the page back where it was (2026-10-15,
  // Riley's iPhone). Safari scrolls the document to bring the bar's field
  // clear of the keyboard, and leaves it there when the keyboard closes —
  // the tree had gone up and did not come back. The scroll position is kept
  // at focus and put back after the blur, unless the person scrolled
  // themselves while typing (a drag or a wheel), in which case where they
  // are is theirs. Once after the blur and once more when the viewport
  // reports its resize (the keyboard's close lands a beat later), nothing
  // after that — no following the viewport. A focus that comes straight back
  // (the second consent box hands the person back to the field) keeps the
  // first position on record and puts nothing back until the field is
  // really let go.
  const scrollAtFocus = useRef<number | null>(null);
  const scrolledWhileTyping = useRef(false);
  useEffect(() => {
    if (!docked) return undefined;
    const onMove = (): void => { if (scrollAtFocus.current !== null) scrolledWhileTyping.current = true; };
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('wheel', onMove, { passive: true });
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('wheel', onMove);
    };
  }, [docked]);
  const noteScroll = (): void => {
    if (!docked || scrollAtFocus.current !== null) return;
    scrollAtFocus.current = window.scrollY;
    scrolledWhileTyping.current = false;
  };
  const restoreScroll = (): void => {
    const at = scrollAtFocus.current;
    if (!docked || at === null) return;
    if (scrolledWhileTyping.current) { scrollAtFocus.current = null; return; }
    const back = (): void => {
      // Focused again in the meantime: not let go yet.
      if (document.activeElement === fieldRef.current) return;
      scrollAtFocus.current = null;
      if (Math.abs(window.scrollY - at) > 2) window.scrollTo({ top: at, behavior: 'auto' });
    };
    const vv = window.visualViewport;
    const once = (): void => { vv?.removeEventListener('resize', once); back(); };
    window.setTimeout(back, 80);
    vv?.addEventListener('resize', once);
    window.setTimeout(() => vv?.removeEventListener('resize', once), 800);
  };

  /** A row chosen: its first line becomes the person's own opening, in the field, ready to add to or send; the next step lights up. */
  const pick = (node: TreeNode): void => {
    const line = node.first_line || '';
    setText(line);
    // The list folds and, for a browser still to tick, the boxes rise in its place.
    setOpen(true);
    setPicked(true);
    lightNext();
    // The field usually still has the keyboard (a row is a button, and on a
    // phone tapping one does not take the focus): leave it be — focusing it
    // again, or moving its caret, makes iOS reveal the field once more
    // (Riley's iPhone, 2026-10-15). Only a field that lost the focus (a mouse
    // click on the row) is focused again, its caret after the line.
    const el = fieldRef.current;
    if (!el || document.activeElement === el) return;
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(line.length, line.length);
    });
  };

  /**
   * The sentence, kept in this browser for the door about to open; the URL
   * gets only its handle (say-handoff.ts: the words never enter a URL). Without
   * storage the door opens on nothing and the person types again.
   */
  const handleFor = (line: string): string => {
    if (!line) return '';
    const handle = stashSay(line);
    if (!handle) trackFailed('talk_handoff', 'storage_unavailable');
    return handle;
  };

  /** The chat, with the sentence as its first turn when there is one — or the registration chooser once the budget is spent. */
  const chatWith = (line: string): string => {
    if (spent) return registerTo;
    const handle = handleFor(line);
    if (!handle) return chatTo;
    const [path, query = ''] = chatTo.split('?');
    const params = new URLSearchParams(query);
    params.set('say', handle);
    return `${path}?${params.toString()}`;
  };

  /** Take a door: with the boxes ticked, the acknowledgment is on file before the page moves (TellingDoors' rule). */
  const take = (leave: () => void): void => {
    setOpen(false);
    if (!asking) { leave(); return; }
    void emberApi.visitorConsent()
      .then(() => noteVisitorConsent(new Date().toISOString()))
      .catch(() => trackFailed('talk_consent', 'record_failed'))
      .finally(leave);
  };

  /** A door pressed while the boxes wait: the sentence waits in the field and the boxes come up, lit. */
  const askFirst = (door: 'write' | 'call'): boolean => {
    if (!waiting) return false;
    askedFor.current = door;
    setAsked(true);
    setOpen(true);
    setHeld(true);
    setGlow('consent');
    return true;
  };

  /** The chat door: the sentence is the first turn; with none, Ember opens. */
  const writeIt = (): void => {
    if (settling) { setPendingDoor('write'); return; }
    if (askFirst('write')) return;
    take(() => navigate(chatWith(said)));
  };

  /** The call, opening on the sentence when there is one — or the registration chooser once the budget is spent. */
  const callWith = (line: string): string => {
    const handle = handleFor(line);
    return handle ? `${callHref}&say=${encodeURIComponent(handle)}` : callHref;
  };

  const talkItOut = (): void => {
    if (settling) { setPendingDoor('call'); return; }
    if (askFirst('call')) return;
    const sentence = said;
    // Full page load on purpose: the call surface is server-rendered.
    take(() => { if (spent) navigate(registerTo); else window.location.assign(callWith(sentence)); });
  };

  // The door kept while the record was read, pressed again with the answer in: the fresh closures see `asking` as it
  // now is, so the boxes come up where they are due and the door opens where they are not.
  useEffect(() => {
    if (settling || !pendingDoor) return;
    setPendingDoor(null);
    if (pendingDoor === 'write') writeIt(); else talkItOut();
  }, [settling, pendingDoor]);

  // The composer's own two boxes ticked with a sentence in the field (2026-10-21 jam): the door is taken for them —
  // the one they pressed, else the chat — with no press of the arrow to find. prod 09-14 → 10-21, the web: of 47
  // browsers that picked a row and never left the homepage, 25 had ticked both boxes and stopped there. A door not
  // pressed is still the door's fact (talk_choose_message / talk_choose_call state=ticked), so the funnels' door
  // step keeps them; one pressed before the boxes is already on record.
  const boxesWaited = useRef(waiting);
  useEffect(() => {
    const ticked = boxesWaited.current && !waiting && asking;
    boxesWaited.current = waiting;
    if (!ticked || !said) return undefined;
    const pressed = asked;
    const door = pressed ? askedFor.current : 'write';
    const t = window.setTimeout(() => {
      if (!pressed) {
        track('ui.clicked', {
          name: door === 'call' ? 'talk_choose_call' : 'talk_choose_message',
          state: 'ticked',
          discovery,
          request_id: doorLogAttrs['data-log-request-id'],
          node_id: doorLogAttrs['data-log-node-id'],
        });
      }
      if (door === 'call') talkItOut(); else writeIt();
    }, 280);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  return (
    <>
    <form
      ref={hostRef}
      className={`hero-composer${docked ? ' is-docked' : ''}`}
      onSubmit={(e) => { e.preventDefault(); writeIt(); }}
      // The hero door's exposure, the same fact the Start here button recorded
      // (landing_talk_open discovery=hero): the entrance came on screen.
      data-log-seen="landing_talk_open"
      {...heroLog}
    >
      {/* The tail and the field are one shape (2026-09-16 second decision):
          Ember's line sits on the field the way a quoted message sits on a
          reply box, so the composer visibly is the conversation's tail. */}
      <div className={`hero-thread${panel ? ' has-sheet' : ''}`}>
        {/* The sheet (2026-10-16 jam 拍板): the list or the boxes, in turn, on
            one night paper that grows up out of the field. */}
        {panel ? (
          <div className="hero-sheet">
            {panel === 'list' ? (
              // Keyed by mode so each list's exposure is its own fact (the seen
              // observer fires once per mounted element).
              <div key={mode} className="hero-list" role="listbox" aria-label="Things people keep coming back to" data-log-seen="hero_light_list" data-log-state={mode}>
                {rows.map((node) => (
                  <button
                    key={node.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="hero-list-row"
                    data-log="hero_light_pick"
                    data-log-node-id={node.id}
                    data-log-state={mode === 'hot' && node.id === here?.id ? 'here' : mode}
                    // The press keeps the focus where it is (2026-10-15): a tap
                    // on a button blurs the field, and on a phone that drops the
                    // keyboard — the bar and the list then slid down under the
                    // finger before the tap completed, and the row's click never
                    // came (typed "hi", picked a row: keyboard gone, field
                    // unchanged — Riley's iPhone). mousedown is where the focus
                    // would move; touch scrolling is untouched.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(node)}
                  >
                    <span className="hero-list-light" aria-hidden />
                    <span className="hero-list-line">{node.first_line}</span>
                    <small className="hero-list-people" aria-label={`${node.people_count} people have stood here`}>
                      <b>{node.people_count.toLocaleString('en-US')}</b>
                      <PersonGlyph />
                    </small>
                  </button>
                ))}
              </div>
            ) : null}
            {panel === 'consent' ? (
              <div className="hero-consent" data-log-seen="talk_consent_gate" {...heroLog}>
                <p className="hero-consent-note">{CONSENT_NOTE}</p>
                {/* The boxes keep the focus where it is too (2026-10-15): ticked
                    from the field, the keyboard stays up rather than dropping and rising. */}
                <label className={`hero-check${glow === 'consent' && !ageOk ? ' hero-next' : ''}`} onMouseDown={(e) => e.preventDefault()}>
                  <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-log="talk_consent_age" />
                  <span>{CONSENT_AGE}</span>
                </label>
                <label className={`hero-check${glow === 'consent' && !tosOk ? ' hero-next' : ''}`} onMouseDown={(e) => e.preventDefault()}>
                  <input type="checkbox" checked={tosOk} onChange={(e) => setTosOk(e.target.checked)} data-log="talk_consent_terms" />
                  <ConsentTerms door="talk" />
                </label>
              </div>
            ) : null}
          </div>
        ) : null}
        {shown ? <HeroTail tail={shown} onEmber={writeIt} log={heroLog} /> : null}
        <div className="hero-field">
          {/* A textarea, not an input (2026-09-27): the Facebook / Instagram
              in-app browser scans every <input> inside a <form> for contact
              fields and offered to autofill a phone number into this one the
              moment it took focus — a bare text input beside a submit button
              is the shape of a one-field lead form, and autocomplete="off"
              does not stop it. It only looks at inputs; the conversation
              page's textarea was never touched. One row that wraps and grows
              with the words (2026-10-26), Enter sends. */}
          <textarea
            ref={fieldRef}
            className="hero-input"
            rows={1}
            value={text}
            onChange={(e) => { setText(e.target.value.replace(/[\r\n]+/g, ' ')); setOpen(true); }}
            onFocus={() => { noteScroll(); setOpen(true); setHeld(true); }}
            // Pressed again after a tap elsewhere let it go, still focused: back in use.
            onClick={() => { setOpen(true); setHeld(true); }}
            onBlur={onFieldBlur}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter') { e.preventDefault(); writeIt(); }
            }}
            placeholder={shown ? returnPlaceholder : placeholder}
            aria-label={shown ? returnPlaceholder : placeholder}
            autoComplete="off"
            enterKeyHint="send"
            maxLength={2000}
          />
          <button
            type="button"
            className={`hero-field-btn hero-voice${glow === 'doors' ? ' hero-next' : ''}`}
            aria-label="Talk it out"
            title="Talk it out"
            aria-disabled={waiting || undefined}
            data-log="talk_choose_call"
            {...heroLog}
            onClick={talkItOut}
          >
            <CallGlyph strokeWidth={1.8} />
          </button>
          <button
            type="submit"
            className={`hero-field-btn hero-send${glow === 'doors' ? ' hero-next' : ''}`}
            aria-label="Start the conversation"
            title="Start the conversation"
            aria-disabled={waiting || undefined}
            data-log="talk_choose_message"
            {...heroLog}
          >
            <SendGlyph />
          </button>
        </div>
      </div>
    </form>
    </>
  );
}

/** The arrow: go. Points right (2026-10-22 Riley), as the apps' does. */
function SendGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

export function PersonGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
}
